import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import Badge from "../../../library/Badge";
import DataTable, {
    DataTableMultiSelectFilter,
    DataTableSingleSelectFilter,
    DataTableStatusFilter,
    ExpandableBulletList,
    TableLink,
} from "../../../library/DataTable";
import IconButton, { StyledActionGroup } from "../../../library/IconButton";
import Modal from "../../../library/Modal";
import { exportTableData } from "../../../../utils/commonFunction";
import useDebounce from "../../../../hooks/useDebounce";
import { getProgrammeStudyPathModuleList } from "../../../../services/ProgramService";
import { useStudyPathModuleList } from "../../../../query/hooks/QueryHooksStudyPaths";
import { getModuleAssignmentPermissions } from "./moduleAssignmentPermissions";
import { getRowsPerPageOptions, MODULE_STATUS_OPTIONS, STANDARD_ROWS_PER_PAGE_SIZES, TERM_OPTIONS, YEAR_OPTIONS } from "./studyPaths.utils";

const EXPORT_COLUMNS = [
    { key: "name", label: "Portal Name" },
    { key: "Code", label: "Code" },
    { key: "credits", label: "Credits" },
    { key: "currentStudyPath", label: "Study Path" },
    { key: "term", label: "Term" },
    { key: "year", label: "Year" },
    { key: "netsuiteProductLabel", label: "NetSuite Product" },
];
const EXPORT_HEADERS = EXPORT_COLUMNS.map((column) => column.label);

/**
 * SPEC4 — Assign Modules modal: search/filter a module group's candidates, see each result's
 * current placement, and assign it into (or unlink it from) a phase.
 * @param {number} [props.phaseId] Phase being edited; falsy on an unsaved (create-mode) phase.
 *   Always staged — picks are handed to `onStageAssign`/`onStageUnassign`, which the caller
 *   (`PhaseForm`) holds in local state until its own Save. `phaseId` here is only used to look up
 *   each candidate's current placement, via `getModuleAssignmentPermissions`.
 * @param {number} props.moduleType 1 = Core, 2 = Foundation, 3 = Elective.
 * @param {Array} [props.stagedModuleIds] Ids already staged, shown as placed.
 */
function AssignModulesModal({ phaseId, moduleType, programmeId, versionId, onClose, onStageAssign, onStageUnassign, stagedModuleIds = [], disabled = false, disabledReason }) {
    const [search, setSearch] = useState("");
    const [term, setTerm] = useState([]);
    const [year, setYear] = useState("");
    const [status, setStatus] = useState([]);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(STANDARD_ROWS_PER_PAGE_SIZES[0]);
    const [sortKey, setSortKey] = useState("name");
    const [sortOrder, setSortOrder] = useState("ASC");
    const debouncedSearch = useDebounce(search, 500);

    const requestData = useMemo(() => ({
        id: phaseId,
        isAssigned: 0,
        module_type: moduleType,
        programme_id: programmeId,
        version_id: versionId,
        search: debouncedSearch,
        term,
        year,
        status,
        page,
        limit: rowsPerPage,
        key: sortKey,
        sort: sortOrder,
    }), [phaseId, moduleType, programmeId, versionId, debouncedSearch, term, year, status, page, rowsPerPage, sortKey, sortOrder]);

    const { data, isLoading } = useStudyPathModuleList(requestData);
    const totalPages = Math.max(1, Math.ceil(data.total / rowsPerPage));

    const handleReset = () => {
        setSearch("");
        setTerm([]);
        setYear("");
        setStatus([]);
        setPage(1);
    };

    const handleAssign = (module) => onStageAssign(module);

    const handleExport = async (fileType) => {
        try {
            const response = await getProgrammeStudyPathModuleList({ ...requestData, exportStatus: "true" });
            const rows = (response.data?.modules ?? []).map((module) => (
                EXPORT_COLUMNS.reduce((row, column) => {
                    row[column.label] = module[column.key] ?? "-";
                    return row;
                }, {})
            ));
            exportTableData(fileType, "Assign Modules", EXPORT_HEADERS, rows);
        } catch (error) {
            console.log("AssignModulesModal.js->handleExport->Error", error);
            Swal.fire("Error", error?.response?.data?.message || "Unable to export modules. Please try again.", "error");
        }
    };

    const handleUnlink = (module) => onStageUnassign(module);

    const columns = [
        {
            key: "name",
            label: "Portal Name",
            render: (module) => (
                <TableLink href={`/program-management-center/modules/module/open/${module.Id}`}>
                    {module.name}
                </TableLink>
            ),
        },
        {
            key: "Code",
            label: "Code",
            render: (module) => module.Code,
        },
        {
            key: "credits",
            label: "Cr",
            render: (module) => `${module.credits}cr`,
        },
        {
            key: "currentStudyPath",
            label: "Study Path",
            render: (module) => (
                module.currentStudyPath === "No Study Path" ? (
                    <Badge tone="destructive" icon="exclamation-triangle">No Study Path</Badge>
                ) : (
                    <Badge tone="tideBlue" icon="link">{module.currentStudyPath}</Badge>
                )
            ),
        },
        {
            key: "term",
            label: "Term",
            render: (module) => module.term,
        },
        {
            key: "year",
            label: "Year",
            render: (module) => module.year,
        },
        {
            key: "linkedCourseCount",
            label: "",
            icon: "books",
            ariaLabel: "Linked courses",
            render: (module) => (
                module.linkedCourseCount === 0 ? (
                    <Badge tone="warning" icon="exclamation-triangle">0</Badge>
                ) : (
                    module.linkedCourseCount
                )
            ),
        },
        {
            key: "netsuiteProductLabel",
            label: "NetSuite Product",
            sortable: false,
            render: (module) => module.netsuiteProductLabel || "-",
        },
        {
            key: "prerequisites",
            label: "Pre-Requisites",
            sortable: false,
            render: (module) => <ExpandableBulletList items={module.prerequisiteNames} />,
        },
        {
            key: "action",
            label: "Action",
            sortable: false,
            width: "1%",
            render: (module) => {
                const permissions = getModuleAssignmentPermissions(module, { phaseId, stagedModuleIds });
                return (
                    <StyledActionGroup>
                        <IconButton
                            icon="plus"
                            ariaLabel={`Assign ${module.name}`}
                            title={`Assign ${module.name}`}
                            variant="solid"
                            disabled={disabled || !permissions.canAssign}
                            disabledReason={permissions.canAssign ? disabledReason : permissions.assignDisabledReason}
                            onClick={() => handleAssign(module)}
                        />
                        <IconButton
                            icon="unlink"
                            ariaLabel={`Unassign ${module.name}`}
                            title={`Unassign ${module.name} from ${module.currentStudyPath}`}
                            variant="destructive"
                            disabled={disabled || !permissions.canUnlink}
                            disabledReason={permissions.canUnlink ? disabledReason : permissions.unlinkDisabledReason}
                            onClick={() => handleUnlink(module)}
                        />
                    </StyledActionGroup>
                );
            },
        },
    ];

    return (
        <Modal title="Assign Modules" onClose={onClose} width={1100} headerTone="brand">
            <DataTable
                columns={columns}
                data={data.data}
                keyField="Id"
                loading={isLoading}
                emptyMessage="No modules match your search and filters."
                sortKey={sortKey}
                sortOrder={sortOrder}
                onSortChange={(field, nextOrder) => {
                    setSortKey(field);
                    setSortOrder(nextOrder);
                }}
                searchValue={search}
                onSearchChange={(value) => { setSearch(value); setPage(1); }}
                onReset={handleReset}
                disabled={disabled}
                disabledReason={disabledReason}
                exportOptions={[
                    { key: "excel", icon: "file-excel", ariaLabel: "Export as Excel", onClick: () => handleExport("xlsx") },
                    { key: "csv", icon: "file-csv", ariaLabel: "Export as CSV", onClick: () => handleExport("csv") },
                    { key: "pdf", icon: "file-pdf", ariaLabel: "Export as PDF", onClick: () => handleExport("pdf") },
                ]}
                filterSlot={(
                    <>
                        <DataTableStatusFilter
                            value={status}
                            onChange={(event) => { setStatus(event.target.value); setPage(1); }}
                            options={MODULE_STATUS_OPTIONS}
                            disabled={disabled}
                            disabledReason={disabledReason}
                        />
                        <DataTableMultiSelectFilter
                            placeholder="Term"
                            value={term}
                            onChange={(event) => { setTerm(event.target.value); setPage(1); }}
                            options={TERM_OPTIONS}
                            disabled={disabled}
                            disabledReason={disabledReason}
                        />
                        <DataTableSingleSelectFilter
                            placeholder="Year"
                            value={year}
                            onChange={(event) => { setYear(event.target.value); setPage(1); }}
                            options={YEAR_OPTIONS}
                            disabled={disabled}
                            disabledReason={disabledReason}
                        />
                    </>
                )}
                pagination={{
                    totalEntries: data.total,
                    rangeStart: (page - 1) * rowsPerPage + 1,
                    rangeEnd: Math.min(page * rowsPerPage, data.total),
                    rowsPerPage,
                    rowsPerPageOptions: getRowsPerPageOptions(data.total),
                    onRowsPerPageChange: (value) => { setRowsPerPage(value); setPage(1); },
                    currentPage: page,
                    totalPages,
                    onPageChange: setPage,
                }}
            />
        </Modal>
    );
}

AssignModulesModal.displayName = "AssignModulesModal";

export default AssignModulesModal;
